import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ContactForm.css';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Italy', 'Spain', 'Netherlands', 'Sweden', 'Norway',
  'Denmark', 'Greece', 'Croatia', 'Montenegro', 'Turkey', 'Macedonia',
  'Serbia', 'Slovenia', 'Portugal', 'Brazil', 'Mexico', 'Japan', 'Other',
];

const SERVER_URL = '';

function ContactForm({ white }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    country: '',
    newsletter: false,
    privacy: false,
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = t('home.newsletter.errorRequired');
    if (!formData.email.trim()) e.email = t('home.newsletter.errorRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = t('home.newsletter.errorEmail');
    if (!formData.privacy) e.privacy = t('home.newsletter.errorPrivacy');
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setStatus('sending');
    try {
      const res = await fetch(`${SERVER_URL}/api/general-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ firstName: '', email: '', country: '', newsletter: false, privacy: false });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const submitLabel = () => {
    if (status === 'sending') return t('home.newsletter.sending');
    if (status === 'success') return t('home.newsletter.sent');
    if (status === 'error') return t('home.newsletter.tryAgain');
    return t('home.newsletter.submit');
  };

  return (
    <section className={`contact-form${white ? ' contact-form--white' : ''}`}>
      <div className="contact-form__inner">
        <h2 className="contact-form__title">{t('home.newsletter.title').toUpperCase()}</h2>
        <p className="contact-form__desc">{t('home.newsletter.body')}</p>

        <form className="contact-form__form" onSubmit={handleSubmit}>
          <div className="contact-form__field">
            <input
              type="text"
              name="firstName"
              placeholder={t('home.newsletter.firstName')}
              className={`contact-form__input${errors.firstName ? ' contact-form__input--error' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
            />
            {errors.firstName && <span className="contact-form__error">{errors.firstName}</span>}
          </div>

          <div className="contact-form__field">
            <input
              type="email"
              name="email"
              placeholder={t('home.newsletter.email')}
              className={`contact-form__input${errors.email ? ' contact-form__input--error' : ''}`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <span className="contact-form__error">{errors.email}</span>}
          </div>

          <div className="contact-form__select-wrapper">
            <select
              name="country"
              className="contact-form__select"
              value={formData.country}
              onChange={handleChange}
            >
              <option value="" disabled>{t('home.newsletter.country')}</option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            <svg className="contact-form__select-arrow" width="14" height="8" viewBox="0 0 14 8" fill="none">
              <path d="M1 1L7 7L13 1" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <label className="contact-form__checkbox">
            <input
              type="checkbox"
              name="newsletter"
              checked={formData.newsletter}
              onChange={handleChange}
            />
            <span className="contact-form__checkmark"></span>
            <span className="contact-form__checkbox-text">
              {t('home.newsletter.consent')}
            </span>
          </label>

          <div className="contact-form__field">
            <label className="contact-form__checkbox">
              <input
                type="checkbox"
                name="privacy"
                checked={formData.privacy}
                onChange={handleChange}
              />
              <span className={`contact-form__checkmark${errors.privacy ? ' contact-form__checkmark--error' : ''}`}></span>
              <span className="contact-form__checkbox-text">
                {t('home.newsletter.termsPrefix')}{' '}
                <a href="/terms-of-use">{t('home.newsletter.termsLink')}</a>{' '}
                {t('home.newsletter.termsMid')}{' '}
                <a href="/privacy-policy">{t('home.newsletter.privacyLink')}</a>.
              </span>
            </label>
            {errors.privacy && <span className="contact-form__error">{errors.privacy}</span>}
          </div>

          <button
            type="submit"
            className="contact-form__submit"
            disabled={status === 'sending' || status === 'success'}
            data-ga-click="contact-form-submit"
          >
            {submitLabel()}
          </button>
        </form>
      </div>
    </section>
  );
}

export default ContactForm;
